import React, { useEffect, useMemo, useState } from 'react'
import { useField } from 'formik'
import { FormattedMessage } from 'react-intl'
import _ from 'lodash'

import { Hide, Show } from '~/components/commons'
import styled from 'styled-components'
import PhotoPlaceloader from '~/assets/img/svg/upload-photo.svg'
import { shade, transparentize } from 'polished'

function UploadPhoto({
  name,
  label,
  helperText = '',
  helperText2 = '',
  onChange,
  errorExtractor,
  required = false,
  showHelperText,
  maxSize,
  style,
  initialImage,
  disabled,
}) {
  const [
    { onChange: handleFormikChange, onBlur, value, ...inputProps },
    meta,
  ] = useField({ name })

  const error = useMemo(
    () => (_.isObject(meta.error) ? errorExtractor(meta.error) : meta.error),
    [meta.error, errorExtractor]
  )

  useEffect(() => {
    setFileUploaded(initialImage)
  }, [initialImage])

  const [fileUploaded, setFileUploaded] = useState(value)

  const hiddenFileInput = React.useRef(null)

  const handleClick = () => {
    hiddenFileInput.current.click()
  }

  const handleRemove = () => {
    setFileUploaded(null)
    onChange(null)
  }

  const handleChange = async event => {
    onChange(event.target.files[0])
    const fileUploaded = await getBase64(event.target.files[0])
    setFileUploaded(fileUploaded)
  }

  const getBase64 = file => {
    return new Promise(resolve => {
      const reader = new FileReader()

      reader.readAsDataURL(file)

      reader.onload = () => {
        resolve(reader.result)
      }
    })
  }

  return (
    <div className="w-100" style={style}>
      <label className="font-small-3" htmlFor={name}>
        <FormattedMessage id={label} />{' '}
        <Show condition={required && _.isEmpty(error)}>*</Show>
        <Show condition={!required && _.isEmpty(error)}>
          (<FormattedMessage id="optional" />)
        </Show>
        <Hide condition={_.isEmpty(error)}>
          <strong className="text-danger">- {error}</strong>
        </Hide>
      </label>
      <div className="d-flex flex-column flex-md-row align-items-center">
        <ImageWrapper maxSize={maxSize}>
          <img
            src={!_.isEmpty(fileUploaded) ? fileUploaded : PhotoPlaceloader}
            alt="Uploaded preview"
          />
        </ImageWrapper>
        <div>
          <BtnsContainer
            {...inputProps}
            className="d-flex justify-content-center flex-column flex-md-row mt-2 mt-md-0"
          >
            <button
              disabled={disabled}
              type="button"
              className="upload btn"
              onClick={handleClick}
              tabIndex={-1}
            >
              <FormattedMessage id="Upload Photo" />
            </button>
            {fileUploaded && (
              <button
                disabled={disabled}
                type="button"
                className="remove btn"
                onClick={handleRemove}
                tabIndex={-1}
              >
                <FormattedMessage id="Remove" />
              </button>
            )}

            <input
              type="file"
              ref={hiddenFileInput}
              accept="image/jpeg, image/png"
              onChange={handleChange}
              style={{ display: 'none' }}
              tabIndex={-1}
            />
          </BtnsContainer>
          {showHelperText && (
            <HelperText>
              <FormattedMessage id={helperText} />
            </HelperText>
          )}
          {showHelperText && (
            <HelperText>
              <FormattedMessage id={helperText2} />
            </HelperText>
          )}
        </div>
      </div>
    </div>
  )
}

export const ImageWrapper = styled.div`
  max-width: ${props => props.maxSize ?? '150px'};
  max-height: ${props => props.maxSize ?? '150px'};
  margin-right: 40px;
  display: flex;

  img {
    max-width: 100%;
    object-fit: cover;
  }
`

export const BtnsContainer = styled.div`
  margin-bottom: 20px;

  .upload {
    background-color: #fff;
    color: #6236ff;
    font-size: 16px;
    font-weight: 500;
    border: 1px solid #6236ff;
    border-radius: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 20px;
    transition-duration: 0.2s;

    &:hover {
      background-color: #7367f0;
      border-color: #7367f0;
      color: white;
    }
  }

  .remove {
    background-color: #fff;
    color: #e02020;
    font-size: 16px;
    font-weight: 500;
    border: none;
    border-radius: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    max-height: 44px;
    margin-right: 10px;

    &:hover {
      background-color: ${transparentize(0.85, '#e02020')};
      color: ${shade(0.3, '#e02020')};
    }
  }
`

export const HelperText = styled.div`
  span {
    font-weight: 500;
    font-size: 13px;
    line-height: 17px;
    color: #a8a8a8;
    max-width: 250px;
  }
`

export default UploadPhoto
